import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";
import { resetLoginDocumentBranding } from "@/lib/tenant-brand";

export default function AcceptOwnershipPage() {
  const [token, setToken] = useState("");
  const [validating, setValidating] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    resetLoginDocumentBranding("Accept ownership · AivoryEMS");
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token") || "";
    setToken(t);
    if (!t) {
      setValidating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/auth/ownership-transfer/validate?token=${encodeURIComponent(t)}`,
        );
        const body = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (res.ok) setPreview(body);
          else setPreview(null);
        }
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const accept = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/ownership-transfer/accept`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Could not accept (${res.status})`);
      setResult(body);
      setDone(true);
      toast.success("You are now the workspace owner");
    } catch (err) {
      toast.error("Acceptance failed", { description: err?.message || "The invitation may have expired." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--bg-base)" }}>
      <div className="w-full max-w-md">
        <div className="mb-10"><Logo /></div>
        <div className="text-overline mb-3">Workspace ownership</div>
        <h1 className="font-heading text-3xl tracking-tight font-medium">Confirm ownership</h1>

        {validating ? (
          <p className="mt-8 text-sm text-[var(--text-secondary)]">Checking invitation…</p>
        ) : done ? (
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-2 text-[var(--primary)]">
              <CheckCircle size={20} />
              <span className="font-medium">Ownership updated</span>
            </div>
            <p className="text-sm text-[var(--text-secondary)]">
              <span className="font-mono">{result?.ownerEmail || preview?.successorEmail}</span> is now the owner of{" "}
              <strong>{preview?.workspaceName || "this workspace"}</strong>. The previous owner remains on the team as an admin.
            </p>
            <Link to="/login" className="btn-primary w-full justify-center mt-4">
              Sign in to admin <ArrowRight size={14} />
            </Link>
          </div>
        ) : !preview ? (
          <div className="mt-8 space-y-4">
            <p className="text-sm text-[var(--text-secondary)]">
              This invitation is invalid or has expired. Ask the current owner to send a new transfer invitation from Settings → Organization.
            </p>
            <Link to="/login" className="btn-secondary w-full justify-center">Back to sign in</Link>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            <div className="surface p-5 border-l-2 border-l-[var(--primary)] text-sm text-[var(--text-secondary)] space-y-2">
              <p>
                <strong className="text-[var(--text-primary)]">{preview.fromEmail}</strong> invited you to become owner of{" "}
                <strong className="text-[var(--text-primary)]">{preview.workspaceName}</strong>.
              </p>
              <p className="text-xs font-mono text-[var(--text-tertiary)]">
                Nominated account: {preview.successorEmail}
              </p>
            </div>
            <ul className="text-xs text-[var(--text-tertiary)] space-y-2 list-disc pl-4">
              <li>Billing and subscription management will move to you.</li>
              <li>You can invite or remove admins from Settings → Team.</li>
              <li>The current owner will become an admin after you accept.</li>
            </ul>
            <button
              type="button"
              className="btn-primary w-full justify-center"
              onClick={accept}
              disabled={busy}
              data-testid="accept-ownership-submit"
            >
              {busy ? "Confirming…" : "Accept ownership"}
            </button>
            <p className="text-center text-xs text-[var(--text-tertiary)]">
              Not you?{" "}
              <Link to="/login" className="text-[var(--primary)] hover:underline">
                Sign in with a different account
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
